import {Alert, Button, Group, List, Modal, Stack, Table, Text} from '@mantine/core'
import {Dropzone} from '@mantine/dropzone'
import {IconDownload, IconUpload} from '@tabler/icons-react'
import {useState} from 'react'

import type {CustomerImportPreview, CustomerImportResult} from './schemas'
import {
    useDownloadCustomerImportTemplate,
    useImportCustomersCsv,
    usePreviewCustomerImport,
} from './use-customer-import'

type CustomerImportModalProps = {
    opened: boolean
    onClose: () => void
}

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024

function formatAction(action: CustomerImportPreview['rows'][number]['action']) {
    return action === 'create_customer_and_contact' ? 'Create customer + contact' : 'Add contact'
}

export function CustomerImportModal({opened, onClose}: CustomerImportModalProps) {
    const downloadTemplate = useDownloadCustomerImportTemplate()
    const previewImport = usePreviewCustomerImport()
    const importCustomers = useImportCustomersCsv()

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<CustomerImportPreview | null>(null)
    const [importResult, setImportResult] = useState<CustomerImportResult | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const validRowCount = preview?.rows.filter((row) => row.status === 'valid').length ?? 0
    const isBusy =
        downloadTemplate.isPending || previewImport.isPending || importCustomers.isPending

    function resetState() {
        setSelectedFile(null)
        setPreview(null)
        setImportResult(null)
        setErrorMessage(null)
    }

    function handleClose() {
        resetState()
        onClose()
    }

    async function handleDownloadTemplate() {
        setErrorMessage(null)
        try {
            await downloadTemplate.mutateAsync()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to download template')
        }
    }

    async function handleFileSelected(file: File) {
        setSelectedFile(file)
        setPreview(null)
        setImportResult(null)
        setErrorMessage(null)

        try {
            const previewResult = await previewImport.mutateAsync(file)
            setPreview(previewResult)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to preview import')
        }
    }

    async function handleImport() {
        if (!selectedFile || validRowCount === 0) {
            return
        }

        setErrorMessage(null)
        try {
            const result = await importCustomers.mutateAsync(selectedFile)
            setImportResult(result)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to import customers')
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Import customers from CSV"
            centered
            size="xl"
        >
            <Stack gap="md">
                <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                        Download the current template, fill in one contact per row, then upload the
                        CSV for preview.
                    </Text>
                    <Button
                        variant="default"
                        leftSection={<IconDownload size={16} />}
                        onClick={() => void handleDownloadTemplate()}
                        loading={downloadTemplate.isPending}
                    >
                        Download template
                    </Button>
                </Group>

                {!importResult && (
                    <Dropzone
                        onDrop={(files) => {
                            const file = files[0]
                            if (file) {
                                void handleFileSelected(file)
                            }
                        }}
                        onReject={() =>
                            setErrorMessage('Invalid file type or size. Upload a CSV up to 25 MB.')
                        }
                        maxSize={MAX_FILE_SIZE_BYTES}
                        accept={['text/csv']}
                        multiple={false}
                        loading={previewImport.isPending}
                        data-testid="customer-import-dropzone"
                    >
                        <Group justify="center" gap="xs" mih={80} style={{pointerEvents: 'none'}}>
                            <Dropzone.Accept>
                                <IconUpload size={24} stroke={1.5} />
                            </Dropzone.Accept>
                            <Dropzone.Reject>
                                <IconUpload size={24} stroke={1.5} />
                            </Dropzone.Reject>
                            <Dropzone.Idle>
                                <IconUpload size={24} stroke={1.5} />
                            </Dropzone.Idle>
                            <div>
                                <Text size="sm" inline>
                                    Drag a CSV here or click to select a file
                                </Text>
                                {selectedFile && (
                                    <Text size="xs" c="dimmed">
                                        Selected: {selectedFile.name}
                                    </Text>
                                )}
                            </div>
                        </Group>
                    </Dropzone>
                )}

                {errorMessage && (
                    <Alert color="red" title="Import error">
                        {errorMessage}
                    </Alert>
                )}

                {preview?.templateOutdated && preview.warning && !importResult && (
                    <Alert color="yellow" title="Outdated template">
                        {preview.warning}
                    </Alert>
                )}

                {preview && !importResult && (
                    <Table withTableBorder withColumnBorders striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Row</Table.Th>
                                <Table.Th>Customer</Table.Th>
                                <Table.Th>Contact</Table.Th>
                                <Table.Th>Phone</Table.Th>
                                <Table.Th>Email</Table.Th>
                                <Table.Th>Title</Table.Th>
                                <Table.Th>Action</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Message</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {preview.rows.map((row) => (
                                <Table.Tr key={row.rowIndex}>
                                    <Table.Td>{row.rowIndex}</Table.Td>
                                    <Table.Td>{row.customerName}</Table.Td>
                                    <Table.Td>{row.contactName}</Table.Td>
                                    <Table.Td>{row.phone}</Table.Td>
                                    <Table.Td>{row.email ?? '—'}</Table.Td>
                                    <Table.Td>{row.title ?? '—'}</Table.Td>
                                    <Table.Td>{formatAction(row.action)}</Table.Td>
                                    <Table.Td>{row.status}</Table.Td>
                                    <Table.Td>{row.error ?? row.warning ?? ''}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}

                {importResult && (
                    <Alert color="green" title="Import complete">
                        <Stack gap="xs">
                            <Text size="sm">
                                Imported {importResult.importedCount} contacts, created{' '}
                                {importResult.customersCreatedCount} customers, and skipped{' '}
                                {importResult.skippedCount} rows.
                            </Text>
                            {importResult.errors.length > 0 && (
                                <>
                                    <Text size="sm" fw={500}>
                                        Skipped rows:
                                    </Text>
                                    <List size="sm" spacing="xs">
                                        {importResult.errors.map((error) => (
                                            <List.Item key={error.rowIndex}>
                                                Row {error.rowIndex}: {error.message}
                                            </List.Item>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Stack>
                    </Alert>
                )}

                <Group justify="flex-end">
                    <Button variant="default" onClick={handleClose}>
                        {importResult ? 'Close' : 'Cancel'}
                    </Button>
                    {!importResult && (
                        <Button
                            onClick={() => void handleImport()}
                            loading={importCustomers.isPending}
                            disabled={!selectedFile || validRowCount === 0 || isBusy}
                        >
                            Import {validRowCount} {validRowCount === 1 ? 'contact' : 'contacts'}
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    )
}
